/** * 報告書登録・編集コンポーネント */
<template>
  <div class="report-editor">
    <ifield-b-modal
      id="report-editor-dialog"
      ref="report-editor-dialog-modal"
      v-bind:title="
        isCreate
          ? $t('theReportEditor.message.createReport')
          : $t('theReportEditor.message.editReport')
      "
      centered
      scrollable
      no-body-border
      size="lg"
      ok-variant="success"
      ignore-enforce-focus-selector=".popover"
      v-bind:ok-title="okTitle"
      v-bind:cancel-title="$t('theReportEditor.action.cancel')"
      v-on:ok.prevent="$_saveReport"
    >
      <loading-mask isFullScreen v-bind:isLoading="isSaving" />
      <!-- フォーム(標準項目) -->
      <form-group
        id="report-form-standard"
        ref="form-standard"
        class="form-group-standard"
        v-bind:mapTypes="$store.state.login.mapTypes"
        v-bind:onIfieldApiError="handleApiError"
        v-on:form-group-value-change="$_onFormStandardValueChange"
      >
        <!-- 報告書プロジェクト -->
        <template v-slot:report_project_id="{ value, setValue }">
          <report-project-selector
            selected-all-label=""
            selected-all-item-to-tail
            v-bind:selected-project-id="value"
            v-bind:select-first-item-on-mounted="isSelectFirstProjectItem"
            v-bind:only-my-project="isReportProjectSelectable"
            v-bind:disabled="!isReportProjectSelectable"
            v-on:change-report-project="setValue"
          />
        </template>
        <!-- 位置選択地図 -->
        <template
          v-slot:[`${geomFieldName}-map`]="{
            idPrefix,
            selectStart,
            onSelectEnd,
          }"
        >
          <map-control
            v-bind="$store.getters.mapControlBinds"
            v-bind:id-prefix="idPrefix"
            v-bind:height="'80vh'"
            v-bind:onIfieldApiError="handleApiError"
            v-on:indoor-level-selected="setActiveIndoorLevelId"
            v-on:indoor-building-reset="unselectedBuilding"
            v-on:menu-layers-visible-changed="
              $store.dispatch('map/setVisible', $event)
            "
            v-on:map-control-initend="selectStart"
            v-on:map-control-layer-selectend="onSelectEnd"
          />
        </template>
      </form-group>
      <!-- フォーム(報告書カラム) -->
      <form-group
        id="report-form-attribute"
        ref="form-attribute"
        class="form-group-attribute"
        v-bind:onIfieldApiError="handleApiError"
      />
      <!-- フォーム(添付ファイル) -->
      <form-group
        id="report-form-attach-files"
        ref="form-attach-files"
        class="form-group-attach-files"
        v-bind:onIfieldApiError="handleApiError"
      />
    </ifield-b-modal>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import ReportProjectSelector from '@/components/AppReportProjectSelector.vue'
import ComponentMountableSwal from '@/lib/ComponentMountableSwal.js'
import ScheduleSummary from '@/components/schedule/AppScheduleSummary.vue'
import EstimatedScheduleList from '@/components/schedule/AppEstimatedScheduleList.vue'
import GeometryUtil from '@/js/util/GeometryUtil.js'
import {
  FUNCTION_TYPE,
  DATE_FORMAT_HYPHEN,
  TIME_FORMAT_MINUTE,
} from '@/constants.js'
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import Schedule from '@/model/Schedule.js'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { toLocalTz, toLocalTzAttribute, toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import { useMapIndoorStore } from '@/composables/map/UseMapIndoorStore'

/**
 * 未選択時の報告書プロジェクトID
 * @type {number}
 */
const UNSELECTED_REPORT_PROJECTS_ID = -1

/**
 * フォームグループのfieldNameに設定する項目名一覧
 */
const FIELD_NAME = {
  /** 報告書プロジェクト */
  REPORT_PROJECT_ID: 'report_project_id',
  /** プレイスID */
  PLACE_ID: 'place_id',
  /** 報告者ID */
  REPORT_USER_ID: 'report_user_id',
  /** タイトル */
  TITLE: 'title',
  /** コメント */
  COMMENT: 'comment',
  /** 報告位置 */
  GEOM: 'geom',
  /** 添付ファイル */
  REPORT_ATTACH_FILES: 'report_attach_files',
}

/**
 * kebab-case を camelCase に変換する
 * @param {String}
 * @returns {String}
 */
const kebabToCamel = (s) => s.replace(/-./g, (x) => x[1].toUpperCase())

/**
 * スケジュールの日時をローカルタイムゾーンに変換する
 * @param {Object} d スケジュール
 * @returns {Object} 日時がローカルタイムゾーンのスケジュール
 */
const toLocalTzSchedule = (d) => ({
  ...d,
  start_at: d.allday_flg ? d.start_at : toLocalTz(d.start_at),
  end_at: d.allday_flg ? d.end_at : toLocalTz(d.end_at),
  create_at: toLocalTz(d.create_at),
  // attributeはこのコンテキストでは使わないので変換しない
})

export default {
  name: 'TheReportEditor',
  components: {
    'report-project-selector': ReportProjectSelector,
  },
  setup(props, context) {
    const { handleApiError, showApiError } = useHandleApiError(props, context)
    const { unselectedBuilding, setActiveIndoorLevelId } = useMapIndoorStore()
    return {
      handleApiError,
      showApiError,
      unselectedBuilding,
      setActiveIndoorLevelId,
    }
  },
  data: function () {
    return {
      /**
       * 報告書ID
       * @type {number}
       */
      reportId: null,
      /**
       * タスク情報
       * @type {Object}
       */
      task: {},
      /**
       * 報告書データ
       * @type {Object}
       */
      report: null,
      /**
       * 選択中の報告書プロジェクト
       */
      currentProject: null,
      /**
       * 報告場所のフィールド名(slot名用)
       * @type {String}
       */
      geomFieldName: FIELD_NAME.GEOM,
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * 保存中の場合はtrue
       * @type {Boolean}
       */
      isSaving: false,
    }
  },
  computed: {
    ...mapGetters('place', {
      placeKeywordTargets: 'keywordTargets',
    }),
    ...mapGetters('userKeywordTarget', {
      userKeywordTargets: 'keywordTargets',
    }),
    /**
     * 新規登録モードの場合はtrue、編集モードの場合はfalse
     * @type {boolean}
     */
    isCreate: function () {
      return !this.reportId
    },
    /**
     * モーダルのOKボタン文言
     * @type {string}
     */
    okTitle: function () {
      return this.isCreate
        ? this.$t('theReportEditor.action.create')
        : this.$t('theReportEditor.action.save')
    },
    /**
     * タスクが指定されている場合はtrue
     * @type {Boolean}
     */
    hasTask: function () {
      return typeof this.task === 'object' && Object.keys(this.task).length > 0
    },
    /**
     * 現在選択中のプロジェクトID
     * @type {Number}
     */
    reportProjectId: function () {
      if (this.report) {
        return this.report.report_project_id
      }
      if (this.task.reportProjectId) {
        return this.task.reportProjectId
      }
      // storageに保存されている値を適用
      let defaultProjectId = this.$store.state.report.defaultProjectId
      return defaultProjectId
    },
    /**
     * 報告書プロジェクトを選択可能にする場合はtrue
     * @type {Boolean}
     */
    isReportProjectSelectable: function () {
      return !this.task.reportProjectId
    },
    /**
     * プロジェクト選択リストの先頭アイテムを使用すべきか否か
     * @type {Boolean}
     */
    isSelectFirstProjectItem: function () {
      if (!this.isCreate) {
        return false
      } else if (this.task.reportProjectId) {
        return false
      }
      return this.$store.state.report.defaultProjectId === null
    },
    /**
     * 現在の報告書カラムデータ
     * @type {Array.<Object>}
     */
    currentReportColumns: function () {
      return (this.currentProject && this.currentProject.report_columns) || []
    },
    /**
     * 項目ラベルMap(key: 項目名(API名), value: 表示ラベル)
     * @return {Map}
     */
    fieldLabelMap: function () {
      return new Map([
        [
          FIELD_NAME.REPORT_PROJECT_ID,
          this.$t('theReportEditor.field.project'),
        ],
        [
          FIELD_NAME.REPORT_USER_ID,
          this.$t('theReportEditor.field.reportUser'),
        ],
        [FIELD_NAME.PLACE_ID, this.$t('theReportEditor.field.place')],
        [FIELD_NAME.TITLE, this.$t('theReportEditor.field.title')],
        [FIELD_NAME.COMMENT, this.$t('theReportEditor.field.comment')],
        [FIELD_NAME.GEOM, this.$t('theReportEditor.field.reportingLocation')],
        [
          FIELD_NAME.REPORT_ATTACH_FILES,
          this.$t('theReportEditor.field.attachFile'),
        ],
      ])
    },
    /**
     * フォーム項目データ(報告者)
     * @type {Object}
     */
    formDatumReportUserId: function () {
      let value = null
      if (this.isCreate) {
        if (this.task.userId) {
          value = this.task.userId
        }
      } else {
        if (this.report) {
          value = this.report[FIELD_NAME.REPORT_USER_ID]
        }
      }

      return {
        fieldName: FIELD_NAME.REPORT_USER_ID,
        label: this.fieldLabelMap.get(FIELD_NAME.REPORT_USER_ID),
        attributeType: ATTRIBUTE_TYPE.SINGLE_USER,
        value,
        validateRule: {
          required: true,
        },
        keywordTarget: this.userKeywordTargets,
        indoorSites: this.$store.getters['indoorSite/indoorSites'],
        targetUserType: 'app',
        onKeywordTargetChange: (data) =>
          this.$store.dispatch('userKeywordTarget/setKeywordTargets', data),
      }
    },
    /**
     * フォーム項目データ(プレイス)
     * @type {Object}
     */
    formDatumPlaceId: function () {
      let value
      let disabled = false
      if (this.isCreate) {
        if (this.task.placeId) {
          value = this.task.placeId
          disabled = true
        }
      } else {
        if (this.report) {
          value = this.report[FIELD_NAME.PLACE_ID]
        }
      }

      return {
        fieldName: FIELD_NAME.PLACE_ID,
        label: this.fieldLabelMap.get(FIELD_NAME.PLACE_ID),
        attributeType: ATTRIBUTE_TYPE.SINGLE_PLACE,
        value,
        disabled,
        keywordTarget: this.placeKeywordTargets,
        onKeywordTargetChange: (data) =>
          this.$store.dispatch('place/setKeywordTargets', data),
      }
    },
    /**
     * AppFormGroupに設定するデータ(標準項目)
     * - カスタム項目は含まれません
     * - fieldNameは"this.report"のプロパティと同じものにしてください
     * @type {Array.<Object>}
     */
    formDataStandard: function () {
      return [
        /** 報告書プロジェクト */
        {
          fieldName: FIELD_NAME.REPORT_PROJECT_ID,
          label: this.fieldLabelMap.get(FIELD_NAME.REPORT_PROJECT_ID),
          attributeType: ATTRIBUTE_TYPE.SLOT,
          value: this.reportProjectId,
        },
        /** 報告者 */
        this.formDatumReportUserId,
        /** プレイス */
        this.formDatumPlaceId,
        /** タイトル */
        {
          fieldName: FIELD_NAME.TITLE,
          label: this.fieldLabelMap.get(FIELD_NAME.TITLE),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: this.isCreate ? null : this.report.title,
          validateRule: {
            required: false,
            max: 128,
          },
          placeholder: this.$t('theReportEditor.placeholder.title'),
        },
        /** コメント */
        {
          fieldName: FIELD_NAME.COMMENT,
          label: this.fieldLabelMap.get(FIELD_NAME.COMMENT),
          attributeType: ATTRIBUTE_TYPE.TEXT,
          value: this.isCreate ? null : this.report.comment,
          placeholder: this.$t('theReportEditor.placeholder.comment'),
        },
        /** 報告場所 */
        {
          fieldName: FIELD_NAME.GEOM,
          label: this.fieldLabelMap.get(FIELD_NAME.GEOM),
          attributeType: ATTRIBUTE_TYPE.LOCATION,
          value: this.isCreate ? null : this.report.geom.geometry,
          validateRule: {
            required: true,
          },
          ...this.$store.state.homePosition,
          modalSize: 'xl',
        },
      ]
    },
    /**
     * AppFormGroupに設定するデータ(報告書カラム)
     * @type {Array.<Object>}
     */
    formDataAttribute: function () {
      return this.currentReportColumns.map((column) =>
        this.$_createFormDataAttribute(column)
      )
    },
    /**
     * AppFormGroupに設定するデータ(添付ファイル)
     * @type {Array.<Object>}
     */
    formDataAttachFiles: function () {
      return [
        {
          fieldName: FIELD_NAME.REPORT_ATTACH_FILES,
          label: this.fieldLabelMap.get(FIELD_NAME.REPORT_ATTACH_FILES),
          attributeType: ATTRIBUTE_TYPE.FILE,
          value: this.isCreate
            ? null
            : { attachFiles: this.report.report_attach_files },
          multiple: true,
          accept:
            'image/png,image/jpeg,image/gif,image/tiff,application/pdf,video/mp4',
          acceptLabel: 'png, jpg, gif, tiff, pdf, mp4',
          recordId: this.isCreate ? null : this.reportId,
        },
      ]
    },
  },
  created: function () {
    this.$_updateIndoorSites()
  },
  mounted: function () {
    this.$log.info('TheReportEditor mounted.')
  },
  methods: {
    /**
     * サイトデータ更新
     */
    $_updateIndoorSites: async function () {
      try {
        this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        })
      }
    },

    /**
     * 報告書プロジェクトレスポンスデータからFormGroupに設定するカスタム項目データを作成する
     * @param {Object} column カスタム項目情報(report_project.report_columns[]の要素)
     * @returns {Object}
     */
    $_createFormDataAttribute(column) {
      const columnData =
        (this.report &&
          this.report.report_columns?.find(
            (item) => item.attribute_id === column.attribute_id
          )) ||
        {}
      let formData = {
        fieldName: `${column.attribute_id}`,
        label: column.attribute_name,
        attributeType:
          // 文字列属性の複数行表示対応のため attribute_type を読み替える
          +column.attribute_type === ATTRIBUTE_TYPE.STRING
            ? ATTRIBUTE_TYPE.TEXT
            : +column.attribute_type,
        value: this.$_convertAttributeToFormValue(columnData, column),
        validateRule: {
          required: column.required,
        },
      }
      // ファイル
      if (column.attribute_type === String(ATTRIBUTE_TYPE.FILE)) {
        formData.accept = 'image/jpeg,image/png,image/gif,image/tiff' // ifieldで受け付けられる画像タイプのみに限定
        formData.acceptLabel = 'png, jpg, gif, tiff'
        formData.recordId = this.reportId
        formData.multiple = true
        formData.isAttribute = true
      }

      // 選択リスト値
      let selectValues = null
      if (column.attribute_type === String(ATTRIBUTE_TYPE.SINGLE_SELECT)) {
        if (column.select_value) {
          // 選択リストの場合
          selectValues = column.select_value.split('|').map((selectValue) => {
            // ラベル情報を持っていないため、textにはvalueと同じ値を設定しておく
            return { value: selectValue, text: selectValue }
          })
        }
        // 必須項目以外の場合は選択肢にnullを持たせる
        if (!column.required) {
          selectValues = [{ value: null, text: '' }].concat(selectValues || [])
        }
      } else if (column.attribute_type === String(ATTRIBUTE_TYPE.CHECK)) {
        if (column.select_value) {
          // 選択リストの場合
          selectValues = column.select_value.split('|').map((selectValue) => {
            // ラベル情報を持っていないため、textにはvalueと同じ値を設定しておく
            return { value: selectValue, text: selectValue }
          })
        }
      } else if (column.attribute_type === String(ATTRIBUTE_TYPE.TOGGLE)) {
        selectValues = []
        if (column.select_value) {
          // 先頭の値をtrueのラベルとする
          let labels = column.select_value.split('|')
          selectValues.push({ value: true, text: labels[0] })
          selectValues.push({ value: false, text: labels[1] })
        }
      }
      formData.selectValues = selectValues

      return formData
    },

    /**
     * 報告書レスポンスデータからFormGroupに設定するカスタム項目の値を取得する
     * @param {Object} data カスタム項目データ(report.report_columns[]の要素)
     * @param {Object} def カスタム項目定義(report_project.report_columns[]の要素)
     * @returns {*} FromGroupに設定する値
     */
    $_convertAttributeToFormValue(data, def) {
      // 項目値が設定されていない場合はnullとする
      if (!data.attribute_value) {
        return null
      }

      let value
      const attributeType = +data.attribute_type
      if (attributeType === ATTRIBUTE_TYPE.DATETIME) {
        value = moment(data.attribute_value).format(
          `${DATE_FORMAT_HYPHEN} ${TIME_FORMAT_MINUTE}`
        )
      } else if (attributeType === ATTRIBUTE_TYPE.CHECK) {
        value = data.attribute_value.split('|')
      } else if (attributeType === ATTRIBUTE_TYPE.TOGGLE) {
        const selectValues = def.select_value ? def.select_value.split('|') : []
        value = !selectValues.indexOf(data.attribute_value)
      } else if (attributeType === ATTRIBUTE_TYPE.FILE) {
        value = data.attribute_value.split('|').map((value) => ({
          attach_file_type: FUNCTION_TYPE.REPORT,
          id: value,
        }))
      } else {
        value = data.attribute_value
      }

      return value
    },

    /**
     * ダイアログを表示する
     *
     * @param {number} reportId 編集対象の報告書ID、新規登録の場合はnull
     * @param {Object} [task={}] タスク情報
     * @property {Number} task.scheduleId 対象スケジュールID
     * @property {Number} task.reportProjectId 対象報告書プロジェクトID
     */
    showModal: async function (reportId, task = {}) {
      this.$_showInitialLoading()
      this.$_initData()
      this.reportId = reportId
      this.task = task
      await this.$_updateMapLayers()

      try {
        if (!this.isCreate) {
          await this.$_getSpecificReport(reportId)
        }
        if (this.reportProjectId) {
          await this.$_getSpecificReportProject(this.reportProjectId)
        }
      } catch {
        this.$_hideInitialLoading()
        return
      }

      this.$refs['report-editor-dialog-modal'].show()
      await this.$nextTick()
      this.$_setData()

      if (this.isCreate && this.task.placeId) {
        this.isPlaceChanged = true
        this.$_setPlaceGeomToReportGeom(this.task.placeId)
      }

      this.$_hideInitialLoading()
    },

    /**
     * マップレイヤー更新
     */
    $_updateMapLayers: async function () {
      try {
        await this.$store.dispatch('mapLayer/updateMapLayersWithThrottle')
      } catch (error) {
        this.handleApiError(error, () =>
          this.$log.warn('failed to fetch maplayers. use fetched before.')
        )
      }
    },

    /**
     * ダイアログを非表示にする
     *
     * @param {Boolean} isTaskCompleted タスクを完了した場合はtrue
     */
    hideModal: function (isTaskCompleted) {
      /**
       * 報告書の編集が完了したときのイベント
       *
       * @param {Boolean} isTaskCompleted タスクを完了した場合はtrue
       */
      this.$emit('report-editend', isTaskCompleted)
      // ローディングを強制的にOFFにする
      this.$_hideInitialLoading()
      this.$_hideSaveLoading()
      this.$refs['report-editor-dialog-modal'].hide()
    },

    /**
     * データを初期化する
     */
    $_initData: function () {
      this.reportId = null
      this.report = null
      this.currentProject = null
    },

    /**
     * 対象IDの報告書データを取得する
     * @param {Number} reportId 報告書ID
     */
    $_getSpecificReport: async function (reportId) {
      const url = RestApiClient.createApiUrl(`report/${reportId}`)
      try {
        const d = await RestApiClient.executeGetApi(url).then(
          ({ data }) => data
        )
        this.report = {
          ...d,
          report_columns: d.report_columns?.map((attr) =>
            toLocalTzAttribute(attr)
          ),
        }
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * 対象IDの報告書プロジェクトデータを取得する
     * @param {Number} projectId 報告書プロジェクトID
     */
    $_getSpecificReportProject: async function (projectId) {
      const url = RestApiClient.createApiUrl(`report_project/${projectId}`)
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.currentProject = json.data
      } catch (error) {
        this.handleApiError(error, (error) => {
          if (error instanceof Response) {
            // 無効なプロジェクトを取得しようとするとエラーが発生してしまう
            // クライアント側では、無効なプロジェクトなのか存在しないプロジェクトなのかを判断できないため、
            // ログを出力するだけにして編集はできるようにしておく
            // TODO:この仕様検討する(編集できても意味なし?)
            this.$log.warn(
              `failed to get project report.(report_project_id=${projectId})`
            )
          } else {
            this.showApiError(error)
            throw error
          }
        })
      }
    },

    /**
     * フォームにデータをセットする(全部)
     */
    $_setData: function () {
      this.$_setDataStandard()
      this.$_setDataAttribute()
      this.$_setDataAttachFiles()
    },

    /**
     * フォームにデータをセットする(標準項目)
     */
    $_setDataStandard: function () {
      const form = this.$refs['form-standard']
      form && form.setData(this.formDataStandard)
    },

    /**
     * フォームにデータをセットする(報告書カラム)
     */
    $_setDataAttribute: function () {
      const form = this.$refs['form-attribute']
      form && form.setData(this.formDataAttribute)
    },

    /**
     * フォームにデータをセットする(添付ファイル)
     */
    $_setDataAttachFiles: function () {
      const form = this.$refs['form-attach-files']
      form && form.setData(this.formDataAttachFiles)
    },

    /**
     * フォームグループの値が変更されたときの処理
     * @param {String} fieldName 項目名
     * @param {Object} value 変更された値
     */
    $_onFormStandardValueChange: function (fieldName, value) {
      if (fieldName === FIELD_NAME.REPORT_PROJECT_ID) {
        this.$_setCurrentReportProject(value)
      }
      if (fieldName === FIELD_NAME.PLACE_ID) {
        // プレイスが変更された場合、報告位置にplaceの位置を設定する
        if (value) {
          this.$_setPlaceGeomToReportGeom(value)
          this.isPlaceChanged = true
        }
      } else if (fieldName === FIELD_NAME.GEOM) {
        // 報告位置が変更された場合、プレイスを未選択に設定する
        !this.isPlaceChanged &&
          this.$_setFormStandardValue(FIELD_NAME.PLACE_ID, null)
        this.isPlaceChanged = false
      }
    },

    /**
     * 報告書プロジェクトが変更された時の処理
     * @param {Number} newProjectId 新たに選択されたプロジェクトID
     */
    $_setCurrentReportProject: async function (newProjectId) {
      // 新規の場合は選択されたプロジェクトIDを保存する
      if (this.isCreate) {
        this.$store.state.report.defaultProjectId = newProjectId
      }

      this.currentProject = null
      if (newProjectId === UNSELECTED_REPORT_PROJECTS_ID) {
        this.currentProject = null
        this.$_setDataAttribute()
      } else {
        await this.$_getSpecificReportProject(newProjectId)
        this.$_setDataAttribute()
      }
    },

    /**
     * プレイスの位置情報を報告位置に設定する
     */
    $_setPlaceGeomToReportGeom: function (placeId) {
      RestApiClient.executeGetApi(
        RestApiClient.createApiUrl(`place/${placeId}`)
      )
        .then((json) => {
          const place = json.data
          let reportGeometry
          if (place.geom.geometry.type === 'Point') {
            reportGeometry = place.geom.geometry
          } else {
            // ポイント以外の場合は重心を報告位置とする
            reportGeometry = {
              type: 'Point',
              coordinates: GeometryUtil.centroid(place.geom.geometry),
            }
          }
          this.$_setFormStandardValue(FIELD_NAME.GEOM, reportGeometry)
        })
        .catch((error) => {
          this.handleApiError(error)
        })
    },

    /**
     * フォーム(標準項目)の指定した項目に値を設定する
     * @param {String} fieldName 項目名
     * @param {*} value 変更された値
     */
    $_setFormStandardValue(fieldName, value) {
      const form = this.$refs['form-standard']
      form && form.setValue(fieldName, value)
    },

    /**
     * 報告書を保存する
     */
    $_saveReport: async function () {
      // 値を取得
      let formValue
      try {
        formValue = this.$_getFormValues()
      } catch {
        return
      }

      this.$_showSaveLoading()
      const requestBody = this.$_makeRequestBody(formValue)

      // サーバーに保存する
      let json
      try {
        if (this.isCreate) {
          json = await this.$_createReport(requestBody)
        } else {
          json = await this.$_updateReport(requestBody)
        }
      } catch {
        this.$_hideSaveLoading()
        return
      }

      await this.$_onSaveSuccess(json)
      this.$_hideSaveLoading()
    },

    /**
     * フォーム値を取得する
     * @returns {Object} フォーム値 (standard, attribute, attachFiles)
     * @throws {Array.<Error>} バリデーションエラー配列
     */
    $_getFormValues: function () {
      const formValue = {}
      const errors = []

      const keys = ['standard', 'attribute', 'attach-files']
      for (const key of keys) {
        try {
          const values = this.$refs[`form-${key}`].getValues()
          formValue[kebabToCamel(key)] = values
        } catch (e) {
          this.$log.error(e)
          errors.push(e)
        }
      }

      if (errors.length) {
        throw errors
      } else {
        return formValue
      }
    },

    /**
     * requestBody 作成
     * @param {Object} formValue フォーム値 (standard, attribute, attachFiles)
     */
    $_makeRequestBody: function (formValue) {
      const body = {
        ...this.$_makeRequestBodyStandard(formValue.standard),
        ...this.$_makeRequestBodyAttribute(formValue.attribute),
        ...this.$_makeRequestBodyAttachFiles(formValue.attachFiles),
      }
      // フォーム項目外を足す
      if (!this.isCreate) {
        body.id = this.reportId
      }
      if (this.task.scheduleId) {
        body.schedule_id = this.task.scheduleId
      }
      return body
    },

    /**
     * requestBody 作成(標準項目分)
     * @param {Array.<Object>} values フォーム値
     * @returns {Object} 標準項目分のrequestBody
     */
    $_makeRequestBodyStandard: function (values) {
      const body = {}
      for (const { name, value } of values) {
        if (!value) {
          body[name] = value
        } else if (name === FIELD_NAME.GEOM) {
          body.lng = value.coordinates[0]
          body.lat = value.coordinates[1]
        } else if (name === FIELD_NAME.REPORT_PROJECT_ID) {
          body[name] = value === UNSELECTED_REPORT_PROJECTS_ID ? null : value
        } else {
          body[name] = value
        }
      }
      return body
    },

    /**
     * requestBody 作成(報告書カラム分)
     * @param {Array.<Object>} values フォーム値
     * @returns {Object} 報告書カラム分のrequestBody
     */
    $_makeRequestBodyAttribute: function (values) {
      if (!values.length) return {}
      return {
        report_columns: values.map(({ name, value }) => {
          let attributeValue = null
          const form = this.formDataAttribute.find(
            (field) => field.fieldName === name
          )
          if (form) {
            const attributeType = +form.attributeType
            if (attributeType === ATTRIBUTE_TYPE.DATETIME) {
              attributeValue = toServerTz(value)
            } else if (attributeType === ATTRIBUTE_TYPE.TOGGLE) {
              attributeValue = value
                ? form.selectValues[0].text
                : form.selectValues[1].text
            } else if (attributeType === ATTRIBUTE_TYPE.FILE) {
              const { attachFiles, tempFiles } = value || {}
              let attachFileIds = []
              let tempFilePaths = []
              if (attachFiles && attachFiles.length) {
                attachFileIds = attachFiles.map((file) => file.id)
              }
              if (tempFiles && tempFiles.length) {
                tempFilePaths = tempFiles.map((file) => file.tempFilePath)
              }
              attributeValue = [...attachFileIds, ...tempFilePaths].join('|')
            } else if (Array.isArray(value)) {
              attributeValue = value.join('|')
            } else {
              attributeValue = value
            }
          }
          return { attribute_id: name, attribute_value: attributeValue }
        }),
      }
    },

    /**
     * requestBody 作成(添付ファイル分)
     * @param {Array.<Object>} values フォーム値
     * @returns {Object} 添付ファイル分のrequestBody
     */
    $_makeRequestBodyAttachFiles: function (values) {
      const body = {}
      const { attachFiles, tempFiles } = values[0].value || {}
      if (attachFiles && attachFiles.length) {
        body.report_attach_files = attachFiles.map((item) => ({
          id: item.id,
        }))
      }
      if (tempFiles && tempFiles.length) {
        body.temp_attach_files = tempFiles.map((item) => ({
          temp_file_path: item.tempFilePath,
        }))
      }
      return body
    },

    /**
     * 報告書作成
     * @param {Object} requestBody リクエストボディ
     * @returns {Object} レスポンスjson
     */
    $_createReport: async function (requestBody) {
      const url = RestApiClient.createApiUrl('report')
      try {
        return await RestApiClient.executePostApi(url, requestBody)
      } catch (error) {
        this.handleApiError(error, this.$_onSaveFailure)
        throw error
      }
    },

    /**
     * 報告書更新
     * @param {Object} requestBody リクエストボディ
     * @returns {Object} レスポンスjson
     */
    $_updateReport: async function (requestBody) {
      const url = RestApiClient.createApiUrl(`report/${this.reportId}`)
      try {
        return await RestApiClient.executePutApi(url, requestBody)
      } catch (error) {
        this.handleApiError(error, this.$_onSaveFailure)
        throw error
      }
    },

    /**
     * 保存成功時の処理
     *
     * @param {Object} json APIレスポンスのjson
     */
    $_onSaveSuccess: async function (json) {
      let isTaskCompleted = false

      if (!json.data) {
        // 通常の保存トーストを表示
        this.$_showSavedToast()
      } else if (json.data.schedule) {
        isTaskCompleted = true
        const { schedule } = json.data
        // タスク完了アラートを表示
        this.$_showTaskCompletedAlert(
          new Schedule(toLocalTzSchedule(schedule), this.$log)
        )
      } else if (
        json.data.estimate_schedules &&
        json.data.estimate_schedules.length > 0
      ) {
        // 完了できるタスクの候補がある場合
        const schedules = json.data.estimate_schedules.map(
          (data) => new Schedule(toLocalTzSchedule(data), this.$log)
        )
        const reportId = json.data.id
        const scheduleTaskCompleted = await this.$_completeTask(
          schedules,
          reportId
        )
        if (scheduleTaskCompleted) {
          this.$_showTaskCompletedAlert(scheduleTaskCompleted)
        } else {
          // タスクの完了に失敗orタスクの完了をキャンセルしたが報告書の登録自体は成功しているので通常の保存トーストを表示
          this.$_showSavedToast()
        }
      } else {
        // 通常の保存トーストを表示
        this.$_showSavedToast()
      }

      this.hideModal(isTaskCompleted)
    },

    /**
     * 保存完了トーストを表示する
     */
    $_showSavedToast: function () {
      this.$toast.fire({
        position: 'top',
        icon: 'success',
        title: this.$t('theReportEditor.message.saved'),
        showConfirmButton: true,
        confirmButtonColor: '#6c757d',
        timer: 2000,
      })
    },

    /**
     * タスク完了アラートを表示する
     *
     * @param {Schedule} schedule 完了したスケジュール
     */
    $_showTaskCompletedAlert: function (schedule) {
      const alertWithComponent = new ComponentMountableSwal()
      alertWithComponent.fire({
        icon: 'success',
        title: this.$t('theReportEditor.message.taskCompleted'),
        component: {
          component: ScheduleSummary,
          parent: this,
          props: {
            schedule: schedule,
          },
        },
      })
    },

    /**
     * タスクを完了する
     *
     * @param {Array.<Object>} schedules 対象スケジュール
     * @param {Number} reportId 作成した報告書ID
     */
    $_completeTask: async function (schedules, reportId) {
      const scheduleId = await this.$_showSelectTaskToCompleteAlert(schedules)
      if (!scheduleId) {
        // タスクを完了しない
        return null
      }

      const targetSchedule = schedules.find(
        (schedule) => schedule.id === scheduleId
      )
      try {
        // タスク完了
        return await this.$_executeCompleteTask(targetSchedule, reportId)
      } catch (error) {
        // 404の時は失敗したスケジュールを省く
        const schedulesFiltered =
          error.status && error.status === 404
            ? schedules.filter((schedule) => schedule.id !== scheduleId)
            : schedules
        // 確認アラートに戻る
        return this.$_completeTask(schedulesFiltered)
      }
    },

    /**
     * 完了するタスクを選択するアラートを表示する
     *
     * @param {Array.<Object>} schedules 対象スケジュール
     */
    $_showSelectTaskToCompleteAlert: async function (schedules) {
      const refKey = 'estimated-schedule-list'
      const alertWithComponent = new ComponentMountableSwal()
      const result = await alertWithComponent.fire({
        icon: 'warning',
        title: this.$t('theReportEditor.confirm'),
        confirmButtonText: this.$t('theReportEditor.action.completeTask'),
        showCancelButton: true,
        cancelButtonText: this.$t('theReportEditor.action.exit'),
        reverseButtons: true,
        width: '36rem',
        component: {
          component: EstimatedScheduleList,
          parent: this,
          props: { schedules },
          ref: refKey,
        },
        preConfirm: () => {
          // 確認ボタンで閉じる前に値を取得する
          return this.$refs[refKey].getValue()
        },
      })
      // アラート内でのアクションで分岐
      if (result.dismiss) {
        return null
      } else if (!result.value) {
        await this.$_showNoScheduleSelectedAlert()
        return await this.$_showSelectTaskToCompleteAlert(schedules)
      } else {
        return result.value
      }
    },

    /**
     * スケジュールが選択されていない旨のアラートを表示する
     */
    $_showNoScheduleSelectedAlert: function () {
      return this.$alert.fire({
        icon: 'error',
        title: this.$t('theReportEditor.message.error'),
        text: this.$t('theReportEditor.message.noScheduleSelected'),
      })
    },

    /**
     * タスクを完了を実行する
     *
     * @param {Schedule} schedule 対象スケジュールデータ
     * @param {Number} reportId 作成した報告書ID
     */
    $_executeCompleteTask: async function (schedule, reportId) {
      const body = { ...schedule.toRequestJSON() }
      // 送信用にサーバータイムゾーンへ戻す
      // attributesに関して取得時にローカルタイムゾーンに変換していないので処理不要
      if (!body.allday_flg) {
        body.start_at = toServerTz(body.start_at)
        body.end_at = toServerTz(body.end_at)
      }

      body.target_users = null
      body.target_organizations = null
      const reportTask = body.tasks.find(
        (task) => task.function_type === FUNCTION_TYPE.REPORT
      )
      reportTask.id = reportId
      reportTask.complete_flg = true

      const url = RestApiClient.createApiUrl(`schedule/${schedule.id}`)
      try {
        await RestApiClient.executePutApi(url, body)
        return schedule
      } catch (error) {
        await this.handleApiError(error, async (error) => {
          if (!(error instanceof Response)) {
            return await this.showApiError(error)
          }
          const json = await error.json()
          return await this.$_showFailedToCompleteTaskAlert(error.status, json)
        })
        throw error
      }
    },

    /**
     * タスク完了失敗アラートを表示する
     */
    $_showFailedToCompleteTaskAlert: function (status, json) {
      let message
      if (status === 404) {
        message = this.$t('theReportEditor.message.scheduleNotFound')
      } else {
        message = json.message
      }

      return this.$alert.fire({
        icon: 'error',
        title: this.$t('theReportEditor.message.failedToCompleteTask'),
        text: message,
      })
    },

    /**
     * 保存失敗時の処理
     *
     * @param {Error} error APIのエラーレスポンス
     */
    $_onSaveFailure: async function (error) {
      if (!(error instanceof Response)) {
        this.showApiError(error)
        return
      }

      const json = await error.json()
      if (this.hasTask) {
        this.$_onSaveFailureTask(error.status, json)
      } else {
        this.$_onSaveFailureNormal(json)
      }
    },

    /**
     * 保存失敗時の処理(タスク)
     *
     * @param {Number} status エラーレスポンスのステータスコード
     * @param {Object} json エラーレスポンスのbody
     */
    $_onSaveFailureTask: async function (status, json) {
      const scheduleError =
        json.errors &&
        json.errors.find((error) => error.field === 'schedule_id')

      if (status === 412) {
        // 対象スケジュールの報告書タスク情報に齟齬がある場合
        this.$_showFailedToSaveAlert([json.message])
        this.$_cancelEdit()
      } else if (scheduleError) {
        // スケジュールIDのせいで保存に失敗した場合
        // 完了てきないのでIDをnullにしておいて報告書のみ保存のときにschedule_idが指定されないようにする
        this.task.scheduleId = null
        // 理由によって確認メッセージを変える
        const isScheduleInvalid = !!scheduleError.invalid_id
        let reasonMessage = isScheduleInvalid
          ? this.$t('theReportEditor.message.scheduleNotFound')
          : scheduleError.message
        const result = await this.$_confirmSaveOnlyReport(reasonMessage)
        if (result) {
          this.$_saveReport()
        } else {
          this.$_cancelEdit()
        }
      } else if (json.errors) {
        const invalidIdErrors = json.errors.filter((error) => error.invalid_id)
        if (invalidIdErrors.length > 0) {
          // 無効なIDが含まれている場合
          // 編集不可になっていて修正できないのでアラートを表示して中断する
          const messages = invalidIdErrors.map((error) => {
            const fieldLabel = this.$_getLabel(error.field)
            if (fieldLabel) {
              return this.$t('theReportEditor.message.disabledId', {
                name: fieldLabel,
              })
            } else {
              return error.message
            }
          })
          this.$_showFailedToSaveAlert(messages)
          this.$_cancelEdit()
        } else {
          this.$_onSaveFailureNormal(json)
        }
      } else {
        this.$_onSaveFailureNormal(json)
      }
    },

    /**
     * 保存失敗時の処理(通常)
     *
     * @param {Object} json エラーレスポンスのbody
     */
    $_onSaveFailureNormal: function (json) {
      if (json.errors) {
        // 項目ごとにエラーを表示する
        const messages = json.errors.map((error) => {
          const fieldLabel = this.$_getLabel(error.field)
          if (fieldLabel) {
            if (error.invalid_id) {
              return this.$t('theReportEditor.message.disabledId', {
                name: fieldLabel,
              })
            } else {
              return this.$t('theReportEditor.message.invalidField', {
                name: fieldLabel,
              })
            }
          } else {
            return error.message
          }
        })
        this.$_showFailedToSaveAlert(messages)
      } else {
        // 項目ごとのエラーが設定されていない場合は、エラーメッセージを表示する
        this.$_showFailedToSaveAlert([json.message])
      }
    },

    /**
     * 保存失敗アラートを表示する
     *
     * @param {Array.<String>} messages エラーメッセージ
     */
    $_showFailedToSaveAlert: function (messages) {
      return this.$alert.fire({
        icon: 'error',
        title: this.$t('theReportEditor.message.failedToSave'),
        html: messages.join('<br/>'),
      })
    },

    /**
     * 報告書だけでも保存するかどうかを確認する
     *
     * @param {String} reasonMessage 理由メッセージ
     * @returns {Boolean} 保存してよい場合はtrue
     */
    $_confirmSaveOnlyReport: async function (reasonMessage) {
      const result = await this.$alert.fire({
        icon: 'warning',
        title: this.$t('theReportEditor.confirm'),
        html: [
          reasonMessage,
          this.$t('theReportEditor.message.askSaveOnlyReport'),
        ].join('<br />'),
        confirmButtonText: this.$t('theReportEditor.action.save'),
        showCancelButton: true,
        cancelButtonText: this.$t('theReportEditor.action.cancel'),
        reverseButtons: true,
      })
      return !!result.value
    },

    /**
     * 指定された項目名のラベルを取得する
     * @param {string} 取得対象の項目名(API名)
     * @return {string} 表示ラベル
     */
    $_getLabel(fieldName) {
      return this.fieldLabelMap.get(fieldName)
    },

    /**
     * 報告書編集を中断する
     */
    $_cancelEdit: function () {
      /**
       * 報告書編集中断イベント
       */
      this.$emit('report-editcancel')
      this.$bvModal.hide('report-editor-dialog')
    },

    /**
     * 初期表示時のローディングを表示する
     */
    $_showInitialLoading: function () {
      this.isLoading = true
    },

    /**
     * 初期表示時のローディングを非表示にする
     */
    $_hideInitialLoading: function () {
      this.isLoading = false
    },

    /**
     * 保存時のローディングを表示する
     */
    $_showSaveLoading: function () {
      this.isSaving = true
    },

    /**
     * 保存時のローディングを非表示にする
     */
    $_hideSaveLoading: function () {
      this.isSaving = false
    },
  },
}
</script>

<style lang="scss" scoped>
.form-group-standard,
.form-group-attribute {
  padding-bottom: 0 !important;
}

::v-deep .report-project-selector {
  margin-top: 0.3rem;
}
</style>
